import {
  Box,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
} from "@chakra-ui/react";
import { Suspense, useEffect, useRef, useState } from "react";
import { isMobile, isTablet, isAndroid } from "react-device-detect";
import { Outlet } from "react-router";
import { useIsSidebarOpen } from "../../hooks/useIsSidebarOpen";
import { useIsOverlayOpen } from "../../screens/reader/hooks/hookstate/useIsOverlayOpen";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { NavBar } from "../navigation/NavBar";
import { SideBar } from "../navigation/SideBar";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { FunnelModal } from "../funnel/FunnelModal";
import { useGaTracking } from "../../hooks/useGaTracking";
import { CollapsibleNavBar } from "../navbar/CollapsibleNavBar";
import { SomethingWentWrong } from "../errors/components/SomethingWentWrong";
import { setUser } from "@sentry/react";
import { useAccountData } from "../../hooks/useAccountData";
import { SlideInMenuCentered } from "../slideInMenu/SlideInMenuCentered";
import { SlideInMenuSidebar } from "../slideInMenu/SlideInMenuSidebar";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { EmailVerificationBanner } from "../../screens/root/components/EmailVerificationBanner";
import { OnboardingBanner } from "../../screens/root/components/OnboardingBanner";
import {
  DEFAULT_SIDEBAR_WIDTH,
  Z_INDEX_NAVBAR,
} from "../../screens/root/constants";
import {
  useSearchParams,
  useLocation,
  ScrollRestoration,
} from "react-router-dom";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { AppPromptDrawer } from "../../screens/root/components/AppPromptDrawer";
import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";
import { useIsInReader } from "../../hooks/useIsInReader";
import { isReaderRoute } from "../../utils/isReaderRoute";
import { useIsTopBarHidden } from "../../hooks/useIsTopBarHidden";
import { useNavigationHistory } from "../../hooks/useNavigationHistory";
import { useCloseModalsOnReaderChange } from "../../hooks/useCloseModalsOnReaderChange";
import { SearchV2Provider } from "../../screens/search/hooks/useSearchContext";

// Routes that use virtualized lists handle their own scroll restoration
// via useVirtualScrollRestoration hook - ScrollRestoration should skip them
const isVirtualizedRoute = (pathname: string) =>
  pathname === "/" ||
  pathname === "/originals" ||
  pathname.startsWith("/channel/") ||
  pathname === "/library" ||
  pathname.startsWith("/browse") ||
  pathname.startsWith("/books") ||
  pathname.startsWith("/creators") ||
  pathname.startsWith("/bundles") ||
  pathname.startsWith("/cms/");

export function RootLayout() {
  useGaTracking(true);
  useNavigationHistory();
  useCloseModalsOnReaderChange();

  const location = useLocation();
  const isSmallScreen = useIsSmallBreakpoint();

  const { openMenu, onCloseMenu } = useSlideInMenu();
  const { isSidebarOpen, setIsSidebarOpen } = useIsSidebarOpen();
  const { isOverlayOpen } = useIsOverlayOpen();
  const isReader = useIsInReader();
  const isTopBarHidden = useIsTopBarHidden();
  const topBarHeight = useTopBarHeight();

  const collapseSidebarForSmalldevices = isSmallScreen || isMobile || isTablet;
  const [defaultedSidebar, setDefaultedSidebar] = useState(false);

  const [searchParams] = useSearchParams();
  const isStandalone = searchParams.get("standalone") === "1";
  const isBlackFridayPurchaseMode = searchParams.get("purchase") === "true";

  useEffect(() => {
    setDefaultedSidebar(true);
    if (defaultedSidebar) return;
    // Always close sidebar when entering reader
    if (isReader) {
      setIsSidebarOpen(false);
      return;
    }
    // For mobile/tablet, close sidebar by default
    if (collapseSidebarForSmalldevices) {
      setIsSidebarOpen(false);
    }
  }, [
    collapseSidebarForSmalldevices,
    defaultedSidebar,
    isReader,
    setIsSidebarOpen,
  ]);

  useEffect(() => {
    if (!isReader) return;
    rootComponentsStore.isSidebarOpen.set(false);
  }, [isReader]);

  // Close slide-in menu on navigation
  const prevMenuLocationRef = useRef<string | null>(null);

  useEffect(() => {
    // Only close menu when navigating to a different page, not when changing reading modes or P2P
    if (
      openMenu &&
      prevMenuLocationRef.current !== null &&
      location.pathname !== prevMenuLocationRef.current
    ) {
      const isInReader = isReaderRoute(location.pathname);
      const wasInReader = isReaderRoute(prevMenuLocationRef.current);

      if (isInReader && wasInReader) {
        // Both are reader paths - check if it's the same release
        const getReleaseKey = (path: string) => path.split("/")[2]; // /read/RELEASE_KEY/...
        const prevReleaseKey = getReleaseKey(prevMenuLocationRef.current);
        const currentReleaseKey = getReleaseKey(location.pathname);

        // Close menu if navigating to a different release
        if (prevReleaseKey !== currentReleaseKey) {
          onCloseMenu();
        }
      } else {
        // Navigating to/from reader or different page entirely
        onCloseMenu();
      }
    }
    prevMenuLocationRef.current = location.pathname;
  }, [location.pathname, openMenu, onCloseMenu]);

  const accountData = useAccountData();
  const userData = accountData?.user;
  useEffect(() => {
    if (accountData) {
      try {
        setUser({
          id: accountData.user?.id,
          email: accountData.user?.email_address,
          guest_id: accountData.guest_id,
        });
      } catch {
        // Sentry may be blocked by ad blockers
      }
    }
  }, [accountData]);

  useDisableBodyScroll();

  // Render only after  initialization
  if (!defaultedSidebar) return null;

  const isValkyraePage = location.pathname.includes("valkyrae");
  const isSettingsAccountPage =
    location.pathname.includes("settings") ||
    location.pathname.includes("settings/account");
  const isGoldPage = location.pathname.includes("gold");
  const isHolidayBundlePage = location.pathname.includes("holiday-bundle");
  const isUltimateXmenBundlePage = location.pathname.includes(
    "ultimate-x-men-bundle",
  );
  const isBlackFridayGoldPageEnabled =
    accountData?.features["black_friday_deal"];
  const isBlackFridayDeal = isGoldPage && isBlackFridayGoldPageEnabled;
  const isGiftCardPage = location.pathname.includes("gift-card");
  const showEmailVerificationBanner =
    userData &&
    !userData.verified &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage &&
    !isSettingsAccountPage;
  const showOnboardingBanner =
    userData &&
    userData.onboarding !== "completed" &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage &&
    !isSettingsAccountPage;
  const shouldHideBanners = isReader;

  const showSidebar =
    !isValkyraePage &&
    !isGoldPage &&
    !isStandalone &&
    !isBlackFridayDeal &&
    !isHolidayBundlePage &&
    !isUltimateXmenBundlePage;

  return (
    <SearchV2Provider>
    <ErrorBoundary errorComponent={<SomethingWentWrong />}>
      {/* ScrollRestoration handles non-virtualized routes.
          Virtualized routes (home, library, browse) use useVirtualScrollRestoration hook.
          Reader manages its own scroll. */}
      <ScrollRestoration
        getKey={(location) => {
          // Skip scroll restoration for virtualized routes and reader
          // (they handle their own scroll restoration)
          if (isVirtualizedRoute(location.pathname)) return null;
          if (isReaderRoute(location.pathname)) return null;
          // Default: restore based on pathname
          return location.pathname;
        }}
      />
      <Flex
        direction="column"
        // h="100dvh"
        data-is-android={isAndroid}
        // overflow="hidden"
        position="relative"
      >
        <FunnelModal />
        {!isStandalone && !isBlackFridayPurchaseMode && (
          <CollapsibleNavBar
            isCollapsed={
              (!isReader && isTopBarHidden && !isSidebarOpen) ||
              (isReader && !isOverlayOpen)
            }
            shouldCollapse={isReader || isSmallScreen}
            boxProps={{
              width: "100%",
              zIndex:
                isReader && isOverlayOpen ? Z_INDEX_NAVBAR : !isReader ? 2 : 0,
              position: isReader ? "fixed" : "sticky",
              top: "0",
              backgroundColor: "charcoal.charcoal",
            }}
          >
            <NavBar />
          </CollapsibleNavBar>
        )}
        {!isHolidayBundlePage &&
          !isUltimateXmenBundlePage &&
          !isValkyraePage &&
          !isBlackFridayDeal &&
          !isSettingsAccountPage &&
          accountData && <AppPromptDrawer />}
        <Flex
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          background="transparent.black.70"
          zIndex={2}
          transition="all .4s ease"
          opacity={openMenu ? 1 : 0}
          visibility={openMenu ? "visible" : "hidden"}
          onClick={onCloseMenu}
        />
        <SlideInMenuSidebar
          active={isReader}
          isTopBarHidden={isTopBarHidden && isSmallScreen}
          openMenu={openMenu}
          onCloseMenu={onCloseMenu}
        />
        <SlideInMenuCentered
          active={!isReader}
          openMenu={openMenu}
          onCloseMenu={onCloseMenu}
        />

        <Flex gap={isGoldPage ? undefined : 6}>
          {showSidebar &&
            (isOverlayOpen || isSmallScreen ? (
              <Modal
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                onOverlayClick={() => setIsSidebarOpen(false)}
                motionPreset="slideInLeft"
                trapFocus={true}
                blockScrollOnMount={false}
                variant="sidebar"
              >
                <ModalOverlay
                  pointerEvents={isOverlayOpen ? "none" : "auto"}
                  width={isOverlayOpen ? "260px" : "100%"}
                  height={`calc(100vh)`}
                  bg={isOverlayOpen ? "charcoal.charcoal" : "blackAlpha.50"}
                  onClick={() => setIsSidebarOpen(false)}
                />
                <ModalContent
                  onClick={() => setIsSidebarOpen(false)}
                  containerProps={{
                    pointerEvents: "none",
                  }}
                  height={`calc(100% - ${topBarHeight.get()}px)`}
                  p={0}
                  m={0}
                >
                  <Box
                    className="styled-scrollbar"
                    sx={{ scrollbarGutter: "stable" }}
                    pointerEvents="all"
                    width={isOverlayOpen ? "100%" : "80vw"}
                    maxWidth="400px"
                    height={"100%"}
                    pl={isOverlayOpen ? 8 : 4}
                    pr={0}
                    overflowY="auto"
                    zIndex={3}
                    background={
                      !isSmallScreen && isOverlayOpen
                        ? "charcoal.charcoal"
                        : "black"
                    }
                  >
                    <SideBar />
                  </Box>
                </ModalContent>
              </Modal>
            ) : isReader ? null : location.pathname.startsWith(
                "/admin-next",
              ) ? null : (
              <Box
                className="styled-scrollbar-hover"
                sx={{ scrollbarGutter: "stable" }}
                display={!isGoldPage ? "block" : "none"}
                width={isSidebarOpen ? `${DEFAULT_SIDEBAR_WIDTH}px` : "78px"}
                ml={8}
                height="100%"
                maxHeight={`calc(100vh - ${topBarHeight.get()}px)`}
                top={`${topBarHeight.get()}px`}
                position="sticky"
                overflowY="auto"
              >
                <SideBar collapsed={!isSidebarOpen} />
              </Box>
            ))}

          <Flex
            flex={1}
            minWidth={0}
            minHeight={`calc(100dvh - ${topBarHeight.get()}px)`}
            sx={{
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Suspense fallback={<Spinner color="blaze.blaze" />}>
              <Outlet />
            </Suspense>
          </Flex>
        </Flex>
        <Box zIndex={isGoldPage || isGiftCardPage ? 2 : undefined}>
          {showEmailVerificationBanner && !shouldHideBanners && (
            <EmailVerificationBanner emailAddress={userData.email_address} />
          )}
          {showOnboardingBanner &&
            !showEmailVerificationBanner &&
            !shouldHideBanners && <OnboardingBanner />}
        </Box>
      </Flex>
    </ErrorBoundary>
    </SearchV2Provider>
  );
}
